/**
 * The UI context lives in its own module so that hot-reloading ui.tsx in
 * development can't leave the provider and its consumers on two different
 * context objects ("useUI must be used inside <UIProvider>").
 */
import { createContext, useContext } from 'react';
import type { DraftItem } from '../../shared/api';
import type { ISODate, Meal } from '../../shared/dates';

export type LogTab = 'describe' | 'search' | 'recent' | 'favorites' | 'custom';
export type LogMode = 'text' | 'voice' | 'photo' | 'label' | 'barcode';

export interface LogOptions {
  meal?: Meal;
  date?: ISODate;
  tab?: LogTab;
  mode?: LogMode;
  drafts?: DraftItem[];
  draftSource?: 'what_fits';
}

export interface Toast {
  id: number;
  message: string;
  tone: 'default' | 'error';
  action?: { label: string; onClick: () => void };
}

export interface UIContextValue {
  today: ISODate;
  selectedDate: ISODate;
  setSelectedDate: (date: ISODate) => void;
  openLog: (opts?: LogOptions) => void;
  openWeighIn: (opts?: { date?: ISODate }) => void;
  openWhatFits: (opts?: { meal?: Meal; date?: ISODate }) => void;
  toast: (message: string, opts?: { tone?: 'default' | 'error'; action?: Toast['action'] }) => void;
}

export const UIContext = createContext<UIContextValue | null>(null);

export function useUI(): UIContextValue {
  const ctx = useContext(UIContext);
  if (!ctx) throw new Error('useUI must be used inside <UIProvider>');
  return ctx;
}
